import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, unref as _unref, renderSlot as _renderSlot, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, withCtx as _withCtx } from "vue"

const _hoisted_1 = ["innerHTML"]
const _hoisted_2 = { class: "dialog-finalize-document__list-description typographies-paragraph-medium-regular" }
const _hoisted_3 = { class: "dialog-finalize-document__list" }
const _hoisted_4 = ["innerHTML"]

import { computed, inject, onMounted, ref } from 'vue';
import type { Translator, PushToast } from 'js/shims-vue';
import { captureException } from '@sentry/vue';
import type { Payment } from 'types/document/documentFormStore.types';
import ComplianceAlert from 'vcomponents/common/document/ComplianceAlert.vue';

enum DialogType {
    FINALIZE = 'finalize',
    VALIDATE = 'validate',
}
interface ValidateDocumentMetadata {
    date: string;
    lastValidationDate: string | null;
    paymentsToLink: Payment[];
}
interface Props {
    validate: (date: string) => Promise<string>;
    type?: DialogType;
    fetchMetadata?: (() => Promise<ValidateDocumentMetadata>) | null;
    forceComplianceAlert?: boolean;
    canUpdateDate?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DialogFinalizeDocument',
  props: {
    validate: {},
    type: { default: DialogType.FINALIZE },
    fetchMetadata: { type: [Function, null], default: null },
    forceComplianceAlert: { type: Boolean, default: false },
    canUpdateDate: { type: Boolean, default: true }
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const props = __props;

const emit = __emit;

const showModal = ref<boolean>(true);
const isSubmitting = ref<boolean>(false);
const isMetadataLoading = ref<boolean>(false);
const currentDate = moment().format('YYYY-MM-DD');
const tempDate = ref<string>(currentDate);
const lastValidationDate = ref<string | null>(null);
const paymentsToLink = ref<Payment[]>([]);

const finalListInfo = computed<string[]>(() => {
    return [
        $t.__(`document.DialogValidateDocument.finalize.final_list_info_1`),
        $t.__(`document.DialogValidateDocument.finalize.final_list_info_2`),
        $t.__(`document.DialogValidateDocument.finalize.final_list_info_3`),
    ];
});
const warningText = computed<string>(() => {
    return props.canUpdateDate
        ? $t.__('document.DialogValidateDocument.finalize.warning_text')
        : $t.__('document.DialogValidateDocument.validate.warning_text_can_not_update_date');
});
const title = computed<string>(() =>
    props.type === DialogType.FINALIZE
        ? $t.__('document.DialogValidateDocument.finalize.title')
        : $t.__('document.DialogValidateDocument.validate.title'),
);
const primaryAction = computed<{ label: string; action: () => void; loading: boolean }>(() => ({
    label:
        props.type === DialogType.FINALIZE
            ? $t.__('document.DialogValidateDocument.finalize.finalize_btn')
            : $t.__('common.actions.confirm'),
    action: handleValidate,
    loading: isSubmitting.value,
}));
const secondaryActions = computed<{ label: string; action: () => void; disabled: boolean }[]>(
    () => [
        {
            label: $t.__('common.actions.cancel'),
            action: (): void => emit('close'),
            disabled: isSubmitting.value,
        },
    ],
);

const calendarOptions = computed(() => {
    if (props.canUpdateDate) {
        return {};
    }
    return {
        maxDate: currentDate,
        minDate: lastValidationDate.value,
    };
});

const isDatePickerLocked = computed<boolean>(
    () => !props.canUpdateDate && lastValidationDate.value === null,
);

const handleValidate = async (): Promise<void> => {
    try {
        isSubmitting.value = true;
        await props.validate(tempDate.value);
    } catch (error: unknown) {
        const typedError = error as { message?: string };
        $pushToast({
            type: CompassEnums.ToastType.ERROR,
            text: typedError?.message ?? '',
        });
    } finally {
        isSubmitting.value = false;
    }
};

onMounted(async () => {
    if (!props.fetchMetadata) {
        return;
    }
    try {
        isMetadataLoading.value = true;
        const metadata = await props.fetchMetadata();
        if (metadata) {
            lastValidationDate.value = metadata.lastValidationDate;
            paymentsToLink.value = metadata.paymentsToLink as Payment[];

            const minDate = metadata.lastValidationDate ?? currentDate;
            tempDate.value = moment(metadata.date).isBefore(minDate) ? minDate : metadata.date;
        }
    } catch (e) {
        captureException(e);
        emit('close');
        $pushToast({
            type: CompassEnums.ToastType.ERROR,
            text: $t.__('settings.sellsymodule.invoicing.conditions.toast_error_data_fetch'),
        });
    } finally {
        isMetadataLoading.value = false;
    }
});

return (_ctx: any,_cache: any) => {
  const _component_CompassSkeleton = _resolveComponent("CompassSkeleton")!
  const _component_CompassAlert = _resolveComponent("CompassAlert")!
  const _component_CompassDatePicker = _resolveComponent("CompassDatePicker")!
  const _component_CompassFormItem = _resolveComponent("CompassFormItem")!
  const _component_CompassModal = _resolveComponent("CompassModal")!

  return (_openBlock(), _createBlock(_component_CompassModal, {
    visible: showModal.value,
    "onUpdate:visible": _cache[1] || (_cache[1] = ($event: any) => ((showModal).value = $event)),
    title: title.value,
    size: _ctx.CompassEnums.ModalSize.SMALL,
    primaryAction: primaryAction.value,
    secondaryActions: secondaryActions.value,
    coloredIconProps: {
            size: _ctx.CompassEnums.ColoredIconSize.LARGE,
            color: _ctx.CompassEnums.ColoredIconColorType.TEAL,
            icon: 'lock-fill',
        },
    class: "dialog-finalize-document",
    onClosed: _cache[2] || (_cache[2] = ($event: any) => (emit('close')))
  }, {
    default: _withCtx(() => [
      (isMetadataLoading.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createVNode(_component_CompassSkeleton),
            _createVNode(_component_CompassSkeleton, {
              size: _ctx.CompassEnums.SkeletonSize.EXTRA_LARGE
            }, null, 8 /* PROPS */, ["size"])
          ], 64 /* STABLE_FRAGMENT */))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            (__props.forceComplianceAlert)
              ? (_openBlock(), _createBlock(ComplianceAlert, { key: 0 }))
              : _createCommentVNode("v-if", true),
            (paymentsToLink.value.length > 0)
              ? (_openBlock(), _createBlock(_component_CompassAlert, {
                  key: 1,
                  type: _ctx.CompassEnums.AlertType.WARNING,
                  description: _unref($t).__('document.DialogValidateDocument.alert_payments_to_link'),
                  showClose: false
                }, null, 8 /* PROPS */, ["type", "description"]))
              : _createCommentVNode("v-if", true),
            _renderSlot(_ctx.$slots, "before-content"),
            _createElementVNode("p", {
              class: "dialog-finalize-document__attention typographies-paragraph-medium-regular",
              innerHTML: warningText.value
            }, null, 8 /* PROPS */, _hoisted_1),
            _createElementVNode("p", _hoisted_2, _toDisplayString(_unref($t).__('document.DialogValidateDocument.finalize.list_description')), 1 /* TEXT */),
            _createElementVNode("ul", _hoisted_3, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(finalListInfo.value, (item, index) => {
                return (_openBlock(), _createElementBlock("li", {
                  key: index,
                  class: "typographies-paragraph-medium-regular",
                  innerHTML: item
                }, null, 8 /* PROPS */, _hoisted_4))
              }), 128 /* KEYED_FRAGMENT */))
            ]),
            _createVNode(_component_CompassFormItem, {
              label: _unref($t).__('document.DialogValidateDocument.finalize.datepicker_label')
            }, {
              default: _withCtx(() => [
                _createVNode(_component_CompassDatePicker, {
                  modelValue: tempDate.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((tempDate).value = $event)),
                  disabled: isDatePickerLocked.value,
                  calendarOptions: calendarOptions.value
                }, null, 8 /* PROPS */, ["modelValue", "disabled", "calendarOptions"])
              ]),
              _: 1 /* STABLE */
            }, 8 /* PROPS */, ["label"])
          ], 64 /* STABLE_FRAGMENT */))
    ]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["visible", "title", "size", "primaryAction", "secondaryActions", "coloredIconProps"]))
}
}

})